import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { fetchQuery, requestSubscription, useMutation } from 'react-relay'
import { graphql } from 'react-relay'
import Env, { GraphQLError } from '@src/RelayEnvironment'
import { formatFiatRaw } from '@src/components/rightSide/walletComponent/WalletComponent'
import { store } from '@src/store'
import { GraphQLSubscriptionConfig } from 'relay-runtime'
import { convertToK } from '@src/utils/functions'
import { toast } from 'react-toastify'
import { BountiesContextQuery, BountiesContextQuery$data } from './__generated__/BountiesContextQuery.graphql'
import { BountiesContextClaimBountyMutation } from './__generated__/BountiesContextClaimBountyMutation.graphql'
import orderBy from 'lodash/orderBy'
import {
  BountiesContextCampaingsQuery,
  BountiesContextCampaingsQuery$data,
} from './__generated__/BountiesContextCampaingsQuery.graphql'
import {
  BountiesContextCampaignSubscription,
  BountiesContextCampaignSubscription$data,
} from './__generated__/BountiesContextCampaignSubscription.graphql'
import { BountiesContextClaimPrizeMutation } from './__generated__/BountiesContextClaimPrizeMutation.graphql'
import { BountiesContextBountyUpdatedSubscription } from './__generated__/BountiesContextBountyUpdatedSubscription.graphql'

import { useAuthStateIs } from '@src/stores/auth'
import { getErrorMessage } from '@src/components/Bounties/Bounty/Bounty.utils'
import { GraphQLErrorResponse } from '@src/components/Bounties/Bounty/Bounty.code.api'

interface CampaignContextType {
  bonus: CampaignType | null
  bountyProgress: string
  isBountyActive: boolean
  bountyWagerLeft: string
  expiresStamp: string | null
  refetchCampaigns: () => void
  claimPrize: (amount: number, virtualId: number) => void

  bounties: BountiesContextQuery$data['listBounties'] | null
  loading: boolean
  error: Error | null
  grabNewBounty: (bountyId: number) => Promise<void>
  getClaimedBountyWithFutureExpiration: () => BountiesContextQuery$data['listBounties'][number] | null
  fetchBounties: () => void
  bountyModalVisible: boolean
  setBountyModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  setBounties: React.Dispatch<React.SetStateAction<BountiesContextQuery$data['listBounties'] | null>>
  updateBountyAfterCodeClaim: (updatedBounty: BountiesContextQuery$data['listBounties'][number]) => void
}

export const BountyStatus = {
  ACTIVE: 'ACTIVE',
  ACTIVE_BUT_BONUS_EXISTS: 'ACTIVE_BUT_BONUS_EXISTS',
  CLAIMED: 'CLAIMED',
  PAUSED: 'PAUSED',
  FINISHED: 'FINISHED',
  RE_CLAIMABLE: 'RE_CLAIMABLE',
} as const

export type BountyStatusType = keyof typeof BountyStatus

const BountiesContext = createContext<CampaignContextType | undefined>(undefined)

export const useBounties = () => {
  const context = useContext(BountiesContext)
  if (context === undefined) {
    throw new Error('useBounties must be used within a BountiesProvider')
  }
  return context
}

type CampaignType =
  | NonNullable<BountiesContextCampaingsQuery$data['userCampaigns']>[number]
  | BountiesContextCampaignSubscription$data['campaignChanged']

export const BountiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLoggedIn = useAuthStateIs('LoggedIn')
  const prevIsActiveBountyRef = useRef<boolean | null>(null)
  const [bountyModalVisible, setBountyModalVisible] = useState(false)
  const [bonus, setBonus] = useState<CampaignType | null>(null)
  const [bountyProgress, setBountyProgress] = useState<string>('0')
  const [isBountyActive, setIsBountyActive] = useState(false)
  const [bountyWagerLeft, setBountyWagerLeft] = useState<string>('0')
  const [expiresStamp, setExpiresStamp] = useState<string | null>(null)
  const [bounties, setBounties] = useState<BountiesContextQuery$data['listBounties'] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [bountyCode, setBountyCode] = useState<string | null>(null)
  const [grabBounty] = useMutation<BountiesContextClaimBountyMutation>(GRAB_BOUNTY_MUTATION)
  const [claimCompletedBounty] = useMutation<BountiesContextClaimPrizeMutation>(CLAIM_PRIZE_MUTATION)

  const fetchBounties = useCallback(
    ({ code }) => {
      setLoading(true)
      fetchQuery<BountiesContextQuery>(Env, BOUNTIES_QUERY, { code }).subscribe({
        next: (response) => {
          const sortedBounties = orderBy(
            response.listBounties,
            [(bounty) => bounty.status === BountyStatus.CLAIMED, 'rating'],
            ['desc', 'asc']
          )
          setBounties(sortedBounties)
          setLoading(false)
        },
        error: (err) => {
          console.error('Error fetching bounties: ', err)
          setError(err)
          setLoading(false)
        },
      })
    },
    [setLoading, setError, setBounties]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    setBountyCode(code)

    fetchBounties({ code })
    const subscriptionConfig = {
      subscription: BOUNTY_UPDATED_SUBSCRIPTION,
      variables: { code },
      onNext: (response) => {
        if (!response?.bountyUpdated) return

        setBounties((prevBounties) => {
          if (!prevBounties) return null

          return prevBounties.map((bounty) =>
            bounty.bountyId === response.bountyUpdated!.bountyId ? { ...response.bountyUpdated } : bounty
          ) as typeof prevBounties
        })
      },
      onError: (error: Error) => {
        console.error('Bounty subscritption error:', error)
      },
    } as GraphQLSubscriptionConfig<BountiesContextBountyUpdatedSubscription>

    const subscriptionDisposable = requestSubscription<BountiesContextBountyUpdatedSubscription>(
      Env,
      subscriptionConfig
    )

    // Clean up the subscription when the component unmounts
    return () => {
      subscriptionDisposable.dispose()
    }
  }, [fetchBounties])

  const grabNewBounty = (bountyId: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      grabBounty({
        variables: { input: { bountyId } },
        onCompleted: (resp) => {
          if (resp.claimBounty?.bounty?.bountyId) {
            setBounties((prevBounties) => {
              if (!prevBounties) return null

              return prevBounties.map((bounty) =>
                bounty.bountyId === resp.claimBounty?.bounty?.bountyId ? { ...resp.claimBounty.bounty } : bounty
              ) as typeof prevBounties
            })

            toast.success('Bounty started successfully!')
            resolve()
          } else {
            toast.error('Failed to start bounty')
            reject(new Error('Failed to start bounty'))
          }
        },
        onError: (err: GraphQLError) => {
          const errorMessage = getErrorMessage(err as GraphQLErrorResponse)

          toast.error(errorMessage)

          reject(err)
        },
      })
    })
  }

  const getClaimedBountyWithFutureExpiration = useCallback(():
    | BountiesContextQuery$data['listBounties'][number]
    | null => {
    if (!bounties) return null

    const now = new Date()

    return (
      bounties.find((bounty) => {
        if (!bounty.expires) return null
        const expires = new Date(bounty.expires)
        return bounty.status === BountyStatus.CLAIMED && expires > now
      }) || null
    )
  }, [bounties])

  const processCampaignData = useCallback((campaign: CampaignType) => {
    if (!campaign) return

    const isActive = !!campaign.active

    // Run fetchBounties when an active campaign becomes inactive
    if (prevIsActiveBountyRef.current && !isActive) {
      fetchBounties({ code: bountyCode })
    }

    // Update the ref to the current state
    prevIsActiveBountyRef.current = isActive

    const expires = campaign?.meta?.expires ?? null
    const multiplier = campaign?.campaign?.payoutMultiplyer || 1
    const currency = campaign?.campaign?.currency || 'USDT'
    const amount = campaign?.amount || 0
    const wagered = campaign?.betSum ?? 0
    const toBeWagered = multiplier * amount
    const wagerLeft = toBeWagered - wagered
    const progress = String(Math.floor((wagered / toBeWagered) * 100))
    const wagerLeftInUSD =
      Number(
        formatFiatRaw(store.getState().wallet.rates, {
          balance: wagerLeft,
          currency: currency,
        })?.toFixed()
      ) || 0
    const formattedWager = convertToK(wagerLeftInUSD)

    setExpiresStamp(expires)
    setBountyWagerLeft(formattedWager)
    setBonus(campaign)
    setBountyProgress(progress)
    setIsBountyActive(isActive)
  }, [])

  const fetchInitialCampaigns = useCallback(() => {
    fetchQuery<BountiesContextCampaingsQuery>(Env, CAMPAIGNS_INITIAL_QUERY, {})
      .toPromise()
      .then((initialData) => {
        if (initialData?.userCampaigns) {
          const activeBountyCampaign = initialData.userCampaigns
            .filter((x) => x !== null)
            .find((userInCampaign) => userInCampaign.active && userInCampaign.campaign?.meta?.bounty === true)

          if (activeBountyCampaign) {
            processCampaignData(activeBountyCampaign)
          } else {
            setIsBountyActive(false)
            setBountyProgress('0')
            setBonus(null)
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching campaigns:', error)
      })
  }, [processCampaignData])

  const claimPrize = useCallback((amount: number, virtualId: number) => {
    claimCompletedBounty({
      variables: {
        input: {
          amount,
          virtualId,
        },
      },
      onCompleted: (resp) => {
        if (resp.claimPlayableGlobalPlaythroughPrice?.success) {
          toast.success('Bounty claimed successfully!')
        }
      },
      onError: (err) => {
        console.error('Claim bonus failed: ', err)
        toast.error('Failed to claim bounty')
      },
    })
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    fetchInitialCampaigns()

    const subscriptionConfig = {
      subscription: CAMPAIGN_CHANGED_SUBSCRIPTION,
      variables: {},
      onNext: (response) => {
        if (response?.campaignChanged) {
          processCampaignData(response.campaignChanged)
        }
      },
      onError: (error: Error) => {
        console.error('Subscription error:', error)
      },
    } as GraphQLSubscriptionConfig<BountiesContextCampaignSubscription>

    const subscriptionDisposable = requestSubscription<BountiesContextCampaignSubscription>(Env, subscriptionConfig)

    return () => {
      subscriptionDisposable.dispose()
    }
  }, [fetchInitialCampaigns, processCampaignData])

  const updateBountyAfterCodeClaim = useCallback((updatedBounty) => {
    setBounties((prevBounties) => {
      if (!prevBounties) return null
      return [updatedBounty, ...prevBounties] as typeof prevBounties
    })
    fetchInitialCampaigns()
  }, [])

  const value = useMemo(
    () => ({
      bountyProgress,
      isBountyActive,
      bountyWagerLeft,
      bonus,
      expiresStamp,
      refetchCampaigns: fetchInitialCampaigns,
      claimPrize,
      bounties,
      loading,
      error,
      grabNewBounty,
      getClaimedBountyWithFutureExpiration,
      fetchBounties,
      bountyModalVisible,
      setBountyModalVisible,
      updateBountyAfterCodeClaim,
    }),
    [
      bountyProgress,
      isBountyActive,
      bountyWagerLeft,
      bonus,
      expiresStamp,
      fetchInitialCampaigns,
      claimPrize,
      bounties,
      loading,
      error,
      grabNewBounty,
      getClaimedBountyWithFutureExpiration,
      fetchBounties,
      bountyModalVisible,
      setBountyModalVisible,
      updateBountyAfterCodeClaim,
    ]
  )

  return <BountiesContext.Provider value={value}>{children}</BountiesContext.Provider>
}

const CAMPAIGN_CHANGED_SUBSCRIPTION = graphql`
  subscription BountiesContextCampaignSubscription {
    campaignChanged {
      meta
      active
      betSum
      amount
      campaign {
        virtualId: id
        currency
        payoutMultiplyer
        type
        meta
      }
    }
  }
`

const CAMPAIGNS_INITIAL_QUERY = graphql`
  query BountiesContextCampaingsQuery {
    userCampaigns {
      meta
      active
      betSum
      amount
      campaign {
        virtualId: id
        payoutMultiplyer
        type
        currency
        meta
      }
    }
  }
`

const CLAIM_PRIZE_MUTATION = graphql`
  mutation BountiesContextClaimPrizeMutation($input: ClaimPlayableGlobalPlaythroughPriceInput!) {
    claimPlayableGlobalPlaythroughPrice(input: $input) {
      success
    }
  }
`

const BOUNTIES_QUERY = graphql`
  query BountiesContextQuery($code: String) {
    listBounties(code: $code) {
      bountyId: id
      amount
      currency
      title
      subtitle
      extendedInfo
      bgImageUrl
      rating
      status
      expires
      claimCount
      claimLimit
    }
  }
`

const BOUNTY_UPDATED_SUBSCRIPTION = graphql`
  subscription BountiesContextBountyUpdatedSubscription($code: String) {
    bountyUpdated(code: $code) {
      bountyId: id
      amount
      currency
      title
      subtitle
      extendedInfo
      bgImageUrl
      rating
      status
      expires
      claimCount
      claimLimit
    }
  }
`

const GRAB_BOUNTY_MUTATION = graphql`
  mutation BountiesContextClaimBountyMutation($input: ClaimBountyInput!) {
    claimBounty(input: $input) {
      bounty {
        bountyId: id
        amount
        currency
        title
        subtitle
        extendedInfo
        bgImageUrl
        rating
        status
        expires
        claimCount
        claimLimit
      }
    }
  }
`
